import { PREVIEW_NS, type PreviewResponseMessage } from "@innoclouds/ui";
import { postToPreview, previewParentOrigin } from "../mode";

export interface BackendReply {
	status: number;
	data: unknown;
}

export type SocketAction = "open" | "send" | "close";

/** Кто отвечает на перехваченные запросы: родительское окно превью или локальный мок */
export interface BackendLink {
	request: (method: string, url: string, body: unknown) => Promise<BackendReply>;
	socket: (id: number, action: SocketAction, url?: string, data?: string) => void;
	acceptsSockets: boolean;
}

const REQUEST_TIMEOUT = 15_000;

export const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const refusal = (status: number, detail: string): BackendReply => ({ status, data: { detail, user_friendly_message: detail } });

export const NO_CONTENT: BackendReply = { status: 204, data: null };
const UNTRUSTED = refusal(503, "Превью не подтверждено: запросы к бэкенду отключены");
const TIMED_OUT = refusal(504, "Превью не ответило на запрос");
const UNREADABLE_BODY = refusal(400, "Превью не смогло передать тело запроса");

const parseUrl = (url: string | URL) => {
	try {
		return new URL(url, window.location.href);
	} catch {
		return null;
	}
};

const hostOf = (url: string | undefined) => (url ? (parseUrl(url)?.host ?? null) : null);

const BACKEND_HOSTS = new Set([hostOf(import.meta.env.VITE_API_URL), hostOf(import.meta.env.VITE_WS_URL)].filter((host): host is string => !!host));

const isBackendHost = (url: URL) => BACKEND_HOSTS.has(url.host);

export const isBackendUrl = (url: string | URL) => {
	const parsed = parseUrl(url);
	return !parsed || parsed.pathname.startsWith("/api/") || isBackendHost(parsed);
};

export const socketTarget = (url: string | URL): "mock" | "refuse" | "native" => {
	const parsed = parseUrl(url);
	if (!parsed) return "refuse";
	if (parsed.pathname.startsWith("/ws/")) return "mock";
	return isBackendHost(parsed) ? "refuse" : "native";
};

export const pathOf = (url: string | URL) => {
	const parsed = parseUrl(url);
	return parsed ? parsed.pathname + parsed.search : "";
};

const parseJson = (text: string): unknown => {
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
};

const describeFile = (file: File) => ({ name: file.name, size: file.size, type: file.type });

const readBody = async (body: unknown): Promise<unknown> => {
	if (body === undefined || body === null) return null;
	if (typeof body === "string") return parseJson(body);
	if (body instanceof URLSearchParams) return Object.fromEntries(body);
	if (body instanceof FormData) return Object.fromEntries([...body].map(([key, value]) => [key, typeof value === "string" ? value : describeFile(value)]));
	if (body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body) || body instanceof ReadableStream)
		return parseJson(await new Response(body as BodyInit).text());
	return body;
};

let lastId = 0;

export const nextPreviewId = () => ++lastId;

const pending = new Map<number, (reply: BackendReply) => void>();

const ask = (method: string, url: string, body: unknown) =>
	new Promise<BackendReply>((resolve) => {
		const id = nextPreviewId();
		const settle = (reply: BackendReply) => {
			window.clearTimeout(timer);
			pending.delete(id);
			resolve(reply);
		};
		const timer = window.setTimeout(() => settle(TIMED_OUT), REQUEST_TIMEOUT);
		pending.set(id, settle);
		try {
			postToPreview({ ns: PREVIEW_NS, type: "request", id, method, url: pathOf(url), body });
		} catch {
			settle(UNREADABLE_BODY);
		}
	});

const previewLink: BackendLink = {
	request: ask,
	socket: (id, action, url, data) => postToPreview({ ns: PREVIEW_NS, type: "socket", id, action, url, data }),
	acceptsSockets: !!previewParentOrigin,
};

let link = previewLink;

export const setBackendLink = (next: BackendLink) => {
	link = next;
};

export const backendLink = () => link;

export const askBackend = async (method: string, url: string, rawBody: unknown): Promise<BackendReply> => {
	if (link === previewLink && !previewParentOrigin) return UNTRUSTED;
	let body: unknown;
	try {
		body = await readBody(rawBody);
	} catch {
		return UNREADABLE_BODY;
	}
	return link.request(method.toUpperCase(), url, body);
};

export const receiveResponse = ({ id, status, data }: PreviewResponseMessage) => pending.get(id)?.({ status, data });

export const replyText = ({ status, data }: BackendReply) => (status === 204 || status === 304 ? "" : JSON.stringify(data ?? null));

export const encodeReply = (reply: BackendReply, responseType: string | undefined) => {
	if (responseType === "blob") return new Blob([replyText(reply)], { type: "application/json" });
	if (responseType === "arraybuffer") return new TextEncoder().encode(replyText(reply)).buffer;
	if (responseType === "text" || responseType === "") return replyText(reply);
	return reply.data;
};
